import React, { useEffect, useState } from "react"
import { formatDateDataGrid } from "src/utils/format-date"
import PropTypes from 'prop-types'
import DatalakeService from "src/services/datalake-service"
import dayjs from "dayjs"
import { TIME_FORMAT } from "src/utils/constants"
import ParameterPaginationTable from "src/components/table/parameter-pagination-table"

const ParameterDatalakeANBIMANegociosTPFTable = (props) => {
    const datalakeService = new DatalakeService()
    const [data, setData] = useState([])
    const [pagination, setPagination] = useState({ count: 0, next: null, previous: null })

    useEffect(() => {
        loadData()
    }, [])

    const loadData = () => {
        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint).then((response) => {
            props.loading(false)

            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        }).catch((err) => {
            props.loading(false)
            props.callbackError(err)
        }).finally(() => {
            props.loading(false)
        })
    }

    const createRow = async () => {
        const body = {
            "ISIN": null,
            "Fonte": null,
            "HorarioReferencia": null,
            "Liquidacao": null,
            "TaxaMax": null,
            "TaxaMed": null,
            "TaxaMin": null,
            "TaxaUltima": null,
            "OfertaCompra": null,
            "OfertaVenda": null,
            "QtNegocios": null,
            "QtNegociada": null,
            "VolumeFinanceiro": null
        }

        props.loading(true)
        return datalakeService.createTableEndpoint(props.endpoint, body).then(() => {
            props.loading(false)
            loadData(0, 25)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const updateRow = async (newRow) => {
        const { ID, ISIN, Fonte, HorarioReferencia, Liquidacao, TaxaMax, TaxaMed, TaxaMin, 
            TaxaUltima, OfertaCompra, OfertaVenda, QtNegocios, QtNegociada, VolumeFinanceiro
        } = newRow

        const body = {
            "ISIN": ISIN,
            "Fonte": Fonte,
            "HorarioReferencia": HorarioReferencia,
            "Liquidacao": Liquidacao,
            "TaxaMax": TaxaMax,
            "TaxaMed": TaxaMed,
            "TaxaMin": TaxaMin,
            "TaxaUltima": TaxaUltima,
            "OfertaCompra": OfertaCompra,
            "OfertaVenda": OfertaVenda,
            "QtNegocios": QtNegocios,
            "QtNegociada": QtNegociada,
            "VolumeFinanceiro": VolumeFinanceiro
        }

        props.loading(true)
        return datalakeService.updateTableEndpoint(props.endpoint, ID, body).then(() => {
            props.loading(false)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }

    const deleteRow = async (row) => {
        props.loading(true)
        const { ID } = row
        return datalakeService.deleteTableEndpoint(props.endpoint, ID).then(() => {
            props.loading(false)
            const listRemoved = data.filter((item) => item.ID !== ID)
            setData(listRemoved)
        }).catch((err) => {
            props.loading(false)
            throw err
        })
    }


    const onFilter = async (filterList, limit, offset) => {
        const query = filterList.map(obj => `${encodeURIComponent(obj.field)}=${encodeURIComponent(obj.value)}`).join('&')

        props.loading(true)
        datalakeService.loadPaginationEndpoint(props.endpoint, query, limit, offset).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, next, previous } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))
            setPagination({ count: count, next: next, previous: previous })
            setData(list)
        })
    }

    const changePage = async (endpoint) => {
        props.loading(true)
        datalakeService.paginate(endpoint).then((response) => {
            props.loading(false)
            const { body } = response
            const { results, count, previous, next } = body
            const list = results.map((obj) => ({ ...obj, id: obj.ID }))

            setPagination({ count: count, next: next, previous: previous })
            setData([])
            setData(list)
        })
    }

    const columns = [
        {
            field: 'ID',
            headerName: '#',
            disableColumnMenu: true,
            disableFilter: true,
            flex: true,
            minWidth: 80,
        },
        {
            field: 'ISIN',
            headerName: 'ISIN',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'Fonte',
            headerName: 'Fonte',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'HorarioReferencia',
            headerName: 'HorarioReferencia',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'time',
            renderCell: (params) => {
                if (!params.value) {
                    return ''
                } 
                return dayjs(params.value).format(TIME_FORMAT)
            },
        },
        {
            field: 'Liquidacao',
            headerName: 'Liquidacao',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
        },
        {
            field: 'TaxaMax',
            headerName: 'TaxaMax',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'TaxaMed',
            headerName: 'TaxaMed',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'TaxaMin',
            headerName: 'TaxaMin',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'TaxaUltima',
            headerName: 'TaxaUltima',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'OfertaCompra',
            headerName: 'OfertaCompra',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'OfertaVenda',
            headerName: 'OfertaVenda',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'QtNegocios',
            headerName: 'QtNegocios',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'QtNegociada',
            headerName: 'QtNegociada',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'VolumeFinanceiro',
            headerName: 'VolumeFinanceiro',
            flex: 1,
            minWidth: 150,
            disableColumnMenu: true,
            editable: true,
            typeComponent: 'number',
        },
        {
            field: 'CriadoEm',
            headerName: 'Data de Criação',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        },
        {
            field: 'AtualizadoEm',
            headerName: 'Data de Atualização',
            flex: true,
            minWidth: 150,
            disableColumnMenu: true,
            disableFilter: true,
            renderCell: (params) => formatDateDataGrid(params)
        }
    ]

    return (
        <div>
            <ParameterPaginationTable
                columns={columns}
                data={data}
                setData={setData}
                createRow={createRow}
                deleteRow={deleteRow}
                updateRow={updateRow}
                pagination={pagination}
                onChangePage={changePage}
                filterFunction={onFilter}
                customFilter={true} />
        </div>
    )
}

ParameterDatalakeANBIMANegociosTPFTable.propTypes = {
    loading: PropTypes.func,
    endpoint: PropTypes.string,
    callbackError: PropTypes.func
}

export default ParameterDatalakeANBIMANegociosTPFTable